import { i18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import {
  type AspectBucket,
  aspectBucketForItem,
  aspectBucketLabel,
  CreativeDetailDrawer,
  type GalleryItem,
  GalleryTile,
  groupCreativeAssets,
} from '../components/creative-tile.js';
import { Mag } from '../components/mag.js';
import { NewCreativeModal } from '../components/new-creative-modal.js';
import { magToast } from '../components/toaster.js';
import { TopNav } from '../components/top-nav.js';
import { type CreativeAssetFormat, runCreativePipeline } from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { founderErrorCopy } from '../lib/error-copy.js';
import { brandsQuery, creativeAssetsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

// Creative playground (M1 s159, sidebar layout s232) — empty state
// shows the brief form inline as the hero. Once there's at least one
// draft, the form retreats into a modal (opened via "+ new draft"
// button) so the gallery dominates. Tile + drawer live in
// components/creative-tile.tsx so the campaign detail page reuses
// them (s159.3).

// s234 — `?asset=<id>` opens the detail drawer on page load so URLs
// are shareable. Empty / missing param = drawer closed. We use a
// validated search param (instead of internal useState) so the URL
// is the single source of truth: typing the URL, hitting back/forward,
// or pasting it into a new tab all open the right drawer.
interface CreativeSearch {
  asset?: string;
}

export const Route = createFileRoute('/brand/$slug/creative')({
  validateSearch: (search): CreativeSearch => {
    const out: CreativeSearch = {};
    if (typeof search['asset'] === 'string' && /^[0-9a-f-]{36}$/i.test(search['asset'])) {
      out.asset = search['asset'];
    }
    return out;
  },
  beforeLoad: async ({ params, context: { queryClient } }) => {
    // Creative playground is dev-only (s265) — in prod, bounce to the
    // brand's home so a typed/bookmarked URL doesn't expose it. The tab
    // is already hidden from the nav (top-nav.tsx); this guards direct
    // navigation. Dev keeps it so we can watch creatives generate.
    if (!import.meta.env.DEV) {
      throw redirect({ to: '/brand/$slug/home', params: { slug: params.slug } });
    }
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    return { brand };
  },
  loader: ({ context }) => context as { brand: { id: string; name: string; slug: string } },
  component: CreativePlaygroundPage,
});

const ASPECT_ORDER: AspectBucket[] = ['9x16', '4x5', '1x1'];

// First-load form lives inline (empty state). Modal owns the post-
// first-draft form via NewCreativeModal. Both share the same format
// list — kept here so the empty-state form doesn't drag the modal
// import into a circular ref.
//
// s234c — `auto` is the default chip and lives at the start of the
// list. When selected, the request omits `formats` from the api call;
// the pipeline's pickFormatFromBrief picks the actual format from
// brief keywords (carousel / story / video / etc.).
type FormatChoice = 'auto' | CreativeAssetFormat;
const ALL_FORMATS: Array<{ value: FormatChoice; label: string; hint?: string }> = [
  { value: 'auto', label: '✨ auto', hint: 'mag picks' },
  { value: 'feed_1x1', label: 'feed · 1:1' },
  { value: 'feed_4x5', label: 'feed · 4:5' },
  { value: 'story_9x16', label: 'story / reel · 9:16' },
  { value: 'carousel_1x1', label: 'carousel · 1:1' },
  { value: 'profile_post', label: 'profile post' },
  { value: 'story_9x16_video', label: 'story / reel video · 9:16', hint: 'mp4 · ~20-30s' },
  { value: 'feed_1x1_video', label: 'feed video · 1:1', hint: 'mp4 · ~20-30s' },
  { value: 'feed_4x5_video', label: 'feed video · 4:5', hint: 'mp4 · ~20-30s' },
];

function CreativePlaygroundPage() {
  const { brand } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  useBrandTitle(brand.slug, ['creative']);
  const queryClient = useQueryClient();

  // s234 — URL is the source of truth for the open drawer; state
  // wrappers below translate caller intent into navigation. Updates
  // are { replace: true } so opening / closing the drawer doesn't
  // pollute the browser history with one entry per click.
  const openAssetId = search.asset ?? null;
  const setOpenAssetId = (id: string | null) => {
    navigate({
      to: '/brand/$slug/creative',
      params: { slug: brand.slug },
      search: id ? { asset: id } : {},
      replace: true,
    });
  };
  const [showNewModal, setShowNewModal] = useState(false);

  // Poll while any row is non-terminal so the founder watches them
  // flip to ready without manual refreshes.
  const assetsQ = useQuery({
    ...creativeAssetsQuery(brand.id),
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const anyPending = data.creativeAssets.some(
        (a) => a.status === 'pending' || a.status === 'rendering',
      );
      return anyPending ? 4_000 : false;
    },
  });
  const assets = assetsQ.data?.creativeAssets ?? [];
  const items = useMemo(() => groupCreativeAssets(assets), [assets]);

  // s232 — group tiles by aspect so 9:16 tiles aren't towering over
  // 1:1 tiles in the same row. Sidebar lists buckets with content;
  // the right pane renders one bucket's grid. ASPECT_ORDER puts the
  // tallest aspect first so the rail anchors on the most visually
  // distinct format.
  const itemsByAspect = useMemo(() => {
    const buckets: Record<AspectBucket, GalleryItem[]> = {
      '9x16': [],
      '4x5': [],
      '1x1': [],
    };
    for (const item of items) {
      buckets[aspectBucketForItem(item)].push(item);
    }
    return buckets;
  }, [items]);
  const nonEmptyBuckets = useMemo(
    () => ASPECT_ORDER.filter((b) => itemsByAspect[b].length > 0),
    [itemsByAspect],
  );
  const [activeBucket, setActiveBucket] = useState<AspectBucket | null>(null);
  // Keep activeBucket in sync with the available buckets. If the
  // current pick empties (asset deleted, brief change) we fall back
  // to the first non-empty bucket so the right pane never goes blank
  // while there's still content to show.
  useEffect(() => {
    if (nonEmptyBuckets.length === 0) {
      if (activeBucket !== null) setActiveBucket(null);
      return;
    }
    if (!activeBucket || !nonEmptyBuckets.includes(activeBucket)) {
      setActiveBucket(nonEmptyBuckets[0] ?? null);
    }
  }, [nonEmptyBuckets, activeBucket]);
  const openAsset = useMemo(
    () => assets.find((a) => a.id === openAssetId) ?? null,
    [assets, openAssetId],
  );
  // When the open asset is part of a carousel, gather every slide
  // (in slide_index order) so the drawer can render the strip.
  const openCarouselSlides = useMemo(() => {
    if (!openAsset?.carouselGroupId) return undefined;
    return assets
      .filter((a) => a.carouselGroupId === openAsset.carouselGroupId)
      .sort((a, b) => (a.slideIndex ?? 0) - (b.slideIndex ?? 0));
  }, [assets, openAsset]);

  const hasDrafts = items.length > 0;
  const showInlineForm = !assetsQ.isPending && !hasDrafts;

  return (
    <main className="min-h-screen bg-[var(--cream)]">
      <TopNav active="creative" />
      <section className="mx-auto max-w-[1120px] px-4 pb-16 pt-8 sm:px-7">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
              <Trans>↘ creative · {brand.name.toLowerCase()}</Trans>
            </p>
            <h1 className="mt-2 font-display text-[28px] sm:text-[34px] font-black leading-[1.05] tracking-[-0.04em]">
              <Trans>tell mag what to design.</Trans>
            </h1>
            {showInlineForm ? (
              <p className="mt-3 max-w-[680px] text-[14.5px] leading-[1.5] text-[var(--ink)]/70">
                <Trans>
                  one or two sentences. she'll pick the template, write the headline + cta, render
                  against your brand. click any draft to see what she wrote + request changes.
                </Trans>
              </p>
            ) : null}
          </div>
          {/* Top-right "+ new draft" button. Only shows once there's
              at least one draft — the empty state hero still wants
              the inline form to be the obvious CTA. */}
          {hasDrafts ? (
            <button type="button" onClick={() => setShowNewModal(true)} className="btn shrink-0">
              <Trans>+ new draft</Trans>
              <span className="btn-arrow">→</span>
            </button>
          ) : null}
        </div>

        {showInlineForm ? (
          <EmptyStateBriefForm
            brandId={brand.id}
            queryClient={queryClient}
            onCreated={(firstAssetId) => setOpenAssetId(firstAssetId)}
          />
        ) : null}

        <div className={showInlineForm ? 'mt-10' : 'mt-8'}>
          {!showInlineForm ? (
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
              {'↘'} <Plural value={assets.length} one="# draft" other="# drafts" />
            </p>
          ) : null}
          {assetsQ.isPending ? (
            <DraftsLoadingState />
          ) : !hasDrafts ? null : (
            <div className="mt-4 grid grid-cols-1 gap-7 md:grid-cols-[224px_1fr] md:gap-10">
              <BucketRail
                buckets={nonEmptyBuckets}
                counts={itemsByAspect}
                active={activeBucket}
                onSelect={setActiveBucket}
              />
              <section className="min-w-0">
                {activeBucket ? (
                  <>
                    <header className="mb-5">
                      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
                        ↘ {aspectBucketLabel(activeBucket)}
                      </p>
                      <h2 className="mt-1 font-display text-[20px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
                        {bucketSubtitle(activeBucket)}
                      </h2>
                    </header>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {itemsByAspect[activeBucket].map((item) => (
                        <GalleryTile
                          key={item.kind === 'carousel' ? item.groupId : item.asset.id}
                          item={item}
                          onOpen={setOpenAssetId}
                        />
                      ))}
                    </div>
                  </>
                ) : null}
              </section>
            </div>
          )}
        </div>
      </section>

      <NewCreativeModal
        open={showNewModal}
        brandId={brand.id}
        onCancel={() => setShowNewModal(false)}
        onCreated={(firstAssetId) => {
          // s233.14 — auto-open the drawer on the just-drafted asset
          // so the founder sees their result immediately instead of
          // hunting for it in the gallery. For carousels, the cover
          // slide is the first id; the drawer + tile group by
          // carouselGroupId so opening any slide loads the whole strip.
          setShowNewModal(false);
          setOpenAssetId(firstAssetId);
        }}
      />

      {openAsset ? (
        <CreativeDetailDrawer
          asset={openAsset}
          {...(openCarouselSlides ? { groupSlides: openCarouselSlides } : {})}
          onClose={() => setOpenAssetId(null)}
        />
      ) : null}
    </main>
  );
}

/** Inline brief form for the empty state — when the founder has zero
 *  drafts, the form IS the hero (don't make them click an extra
 *  button just to get started). Once there's content this disappears
 *  and the same UX moves to the NewCreativeModal.
 *
 *  s233.14 — single-select format + onCreated bubbles the new asset
 *  id so the route can auto-open the drawer on it. */
function EmptyStateBriefForm({
  brandId,
  queryClient,
  onCreated,
}: {
  brandId: string;
  queryClient: ReturnType<typeof useQueryClient>;
  onCreated: (firstAssetId: string) => void;
}) {
  const { t } = useLingui();

  const [brief, setBrief] = useState('');
  const [format, setFormat] = useState<FormatChoice>('auto');
  const [pendingClarification, setPendingClarification] = useState<{
    question: string;
    originalBrief: string;
  } | null>(null);
  const [clarificationAnswer, setClarificationAnswer] = useState('');

  const drafting = useMutation({
    mutationFn: async (overrideBrief?: string) => {
      const text = (overrideBrief ?? brief).trim();
      if (!text) throw new Error('brief is empty');
      // s234c — 'auto' → don't constrain the pipeline; backend picks
      // via pickFormatFromBrief keyword heuristic.
      const formats = format === 'auto' ? undefined : [format];
      return runCreativePipeline(brandId, {
        brief: text,
        ...(formats ? { formats } : {}),
      });
    },
    onSuccess: async (result, sentBrief) => {
      if (result.kind === 'clarification') {
        setPendingClarification({
          question: result.question,
          originalBrief: (sentBrief as string | undefined) ?? brief,
        });
        return;
      }
      await refreshQueries(queryClient, ['creative-assets']);
      magToast.success(
        i18n._(msg`mag drafted it.`),
        i18n._(msg`template ${result.templateKey} · opening for review.`),
      );
      const firstId = result.creativeAssetIds[0];
      setBrief('');
      setPendingClarification(null);
      setClarificationAnswer('');
      if (firstId) onCreated(firstId);
    },
    onError: (err) => {
      magToast.error("mag couldn't draft.", founderErrorCopy(err));
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (drafting.isPending) return;
    drafting.mutate(undefined);
  };

  const handleClarificationAnswer = (e: FormEvent) => {
    e.preventDefault();
    if (drafting.isPending || !pendingClarification) return;
    const answer = clarificationAnswer.trim();
    if (!answer) return;
    const merged = `${pendingClarification.originalBrief}\n\nMAG ASKED: ${pendingClarification.question}\nFOUNDER ANSWERED: ${answer}`;
    drafting.mutate(merged);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="mt-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-4 shadow-[4px_4px_0_var(--spell)]"
      >
        <div className="flex items-start gap-3">
          <Mag size={32} mood="casting" />
          <div className="flex-1">
            <label className="field">
              <span className="field-label">
                <Trans>brief</Trans>
              </span>
              <textarea
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder={t`e.g. 'a post about how tracking ad spend nightly saved us $4k/mo' or 'a hook about saving hours not standing in lines'`}
                rows={3}
                disabled={drafting.isPending}
                maxLength={2000}
              />
            </label>
            <div className="mt-3">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
                {'↘'} <Trans>formats</Trans>
              </span>
              <div className="mt-2 flex flex-wrap gap-2">
                {ALL_FORMATS.map((f) => {
                  const active = format === f.value;
                  return (
                    <button
                      key={f.value}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setFormat(f.value)}
                      disabled={drafting.isPending}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12.5px] transition disabled:opacity-50 ${
                        active
                          ? 'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] font-bold shadow-[2px_2px_0_var(--ink)]'
                          : 'border-[1.5px] border-[var(--line-strong)] bg-white font-medium text-[var(--ink)]/75'
                      }`}
                    >
                      {f.label}
                      {f.hint ? (
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.1em] text-[var(--ink)]/45">
                          {f.hint}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end">
              <button
                type="submit"
                disabled={drafting.isPending || !brief.trim()}
                className="btn disabled:opacity-60"
              >
                {drafting.isPending ? (
                  <Trans>mag is drafting…</Trans>
                ) : (
                  <Trans>draft + render</Trans>
                )}
                <span className="btn-arrow">→</span>
              </button>
            </div>
          </div>
        </div>
      </form>

      {pendingClarification ? (
        <form
          onSubmit={handleClarificationAnswer}
          className="mt-4 rounded-2xl border-[1.5px] border-[var(--plum)] bg-white p-4 shadow-[4px_4px_0_var(--plum)]"
        >
          <div className="flex items-start gap-3">
            <Mag size={32} mood="confused" />
            <div className="flex-1">
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
                {'↘'} <Trans>mag's asking</Trans>
              </span>
              <p className="mt-1 font-display text-[16px] font-bold leading-snug tracking-[-0.01em] text-[var(--ink)]">
                {pendingClarification.question}
              </p>
              <p className="mt-1 text-[12.5px] text-[var(--ink)]/65">
                <Trans>she won't make this up. give her the specifics and she'll draft it.</Trans>
              </p>
              <label className="field mt-3">
                <textarea
                  value={clarificationAnswer}
                  onChange={(e) => setClarificationAnswer(e.target.value)}
                  rows={3}
                  disabled={drafting.isPending}
                  maxLength={2000}
                />
              </label>
              <div className="mt-3 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setPendingClarification(null);
                    setClarificationAnswer('');
                  }}
                  disabled={drafting.isPending}
                  className="link-quiet text-[12px] disabled:opacity-50"
                >
                  <Trans>cancel</Trans>
                </button>
                <button
                  type="submit"
                  disabled={drafting.isPending || !clarificationAnswer.trim()}
                  className="btn disabled:opacity-60"
                >
                  {drafting.isPending ? (
                    <Trans>mag is drafting…</Trans>
                  ) : (
                    <Trans>send + draft</Trans>
                  )}
                  <span className="btn-arrow">→</span>
                </button>
              </div>
            </div>
          </div>
        </form>
      ) : null}
    </>
  );
}

// Mag-voice subtitle per bucket — mirrors the pattern settings uses
// in its rail (one-line, lowercase, dry). Surfaces in the right
// pane's header so the founder reads the bucket like a deliberate
// section, not just a filter chip.
function bucketSubtitle(bucket: AspectBucket): string {
  if (bucket === '9x16') return i18n._(msg`reels + stories.`);
  if (bucket === '4x5') return i18n._(msg`portrait feed posts.`);
  return i18n._(msg`square posts + carousels.`);
}

function BucketRail({
  buckets,
  counts,
  active,
  onSelect,
}: {
  buckets: AspectBucket[];
  counts: Record<AspectBucket, GalleryItem[]>;
  active: AspectBucket | null;
  onSelect: (b: AspectBucket) => void;
}) {
  // Same chiclet treatment the settings rail uses — inactive items
  // are quiet; active gets spell-yellow bg + ink border + plum
  // shadow + a right arrow. Buttons (not Links) because the page
  // routing doesn't change.
  const inactive = [
    'group flex items-center gap-2.5 rounded-md text-left',
    'border border-transparent px-2.5 py-2',
    'transition-all duration-150',
    'hover:border-[var(--line)] hover:bg-white',
  ].join(' ');
  const activeCls = [
    'group flex items-center gap-2.5 rounded-md text-left',
    'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] px-2.5 py-2',
    'shadow-[2px_2px_0_var(--plum)] transition-all duration-150',
  ].join(' ');
  return (
    <aside>
      <nav className="flex flex-col gap-1 md:sticky md:top-20">
        {buckets.map((bucket) => {
          const isActive = bucket === active;
          return (
            <button
              key={bucket}
              type="button"
              onClick={() => onSelect(bucket)}
              className={isActive ? activeCls : inactive}
            >
              <BucketGlyph bucket={bucket} active={isActive} />
              <span className="min-w-0 flex-1">
                <span className="block font-display text-[13px] font-semibold leading-tight text-[var(--ink)]">
                  {aspectBucketLabel(bucket)}
                </span>
                <span className="mt-0.5 block text-[11px] leading-snug text-[var(--ink)]/55">
                  <Plural value={counts[bucket].length} one="# draft" other="# drafts" />
                </span>
              </span>
              <span
                aria-hidden="true"
                className={`font-mono text-[12px] transition-opacity duration-150 ${
                  isActive ? 'text-[var(--ink)]/70' : 'text-[var(--ink)]/0'
                }`}
              >
                →
              </span>
            </button>
          );
        })}
      </nav>
    </aside>
  );
}

/** Tiny shape glyph matching each bucket's aspect ratio. Decorative;
 *  the rail label carries the accessible text. Same line weight as
 *  the settings rail glyphs so the two surfaces feel kin. */
function BucketGlyph({ bucket, active }: { bucket: AspectBucket; active: boolean }) {
  const tint = active ? 'text-[var(--ink)]' : 'text-[var(--ink)]/65';
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': 'true' as const,
    className: `shrink-0 ${tint}`,
  };
  if (bucket === '9x16') {
    return (
      <svg {...common}>
        <title>
          <Trans>story 9:16</Trans>
        </title>
        <rect x="5" y="2" width="6" height="12" rx="1" />
      </svg>
    );
  }
  if (bucket === '4x5') {
    return (
      <svg {...common}>
        <title>
          <Trans>portrait 4:5</Trans>
        </title>
        <rect x="4" y="2.5" width="8" height="11" rx="1" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <title>
        <Trans>square 1:1</Trans>
      </title>
      <rect x="3" y="3" width="10" height="10" rx="1" />
    </svg>
  );
}

/** First-paint placeholder while the creative-assets query resolves.
 *  Replaces the misleading "nothing drafted yet" copy that flashed
 *  during the loading window. Mirrors the rail/grid skeleton so the
 *  layout doesn't shift when real data arrives. */
function DraftsLoadingState() {
  return (
    <div className="mt-4 grid grid-cols-1 gap-7 md:grid-cols-[224px_1fr] md:gap-10">
      <aside className="flex flex-col gap-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-12 animate-pulse rounded-md border border-transparent bg-[var(--ink)]/5 px-2.5 py-2"
          />
        ))}
      </aside>
      <section className="min-w-0">
        <div className="mb-5 h-7 w-48 animate-pulse rounded-md bg-[var(--ink)]/5" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="aspect-square animate-pulse rounded-2xl border-[1.5px] border-[var(--line)] bg-white"
            />
          ))}
        </div>
        <p className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.14em] text-[var(--ink)]/40">
          {'↘'} <Trans>mag is pulling your drafts…</Trans>
        </p>
      </section>
    </div>
  );
}
